import type { PointerEvent as ReactPointerEvent } from 'react'
import {
  FROZEN_OBJECT_CURSOR,
  HANDLE_BORDER_WIDTH,
  HANDLE_SIZE,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import type { ResizeHandle } from '@/lib/boundaries/bounding-box-boundary'
import type { SelectionChromePreview } from '@/lib/shapes/selection-chrome-preview'
import { getResizeCursorForHandle } from '@/lib/transforms/selection-resize-cursor'

import { HANDLE_HIT_PX } from '../constants/chrome-hit-sizes'
import { resolveDecorationOpacity } from '../helpers/chrome-appearance'

/** The eight resize squares at the frame's corners and midpoints, each with its grab zone. */
export function ChromeResizeDots({
  geometry,
  strokeColor,
  geometryInactive,
  interactive,
  beginResize,
}: {
  geometry: SelectionChromePreview
  strokeColor: string
  geometryInactive: boolean
  interactive: boolean
  beginResize: (key: ResizeHandle) => (event: ReactPointerEvent<SVGElement>) => void
}) {
  return (
    <>
      {geometry.handles.map((handle) => (
        // Rotated with the frame, same as `PortSquare`'s numbered square — the visible square's
        // edges stay flush with the frame's own rotated edges instead of standing screen-aligned
        // at a merely-correctly-rotated anchor point (WMPCM-9223).
        <g
          key={handle.key}
          transform={`translate(${handle.x}, ${handle.y}) rotate(${geometry.rotationDeg})`}
        >
          <rect
            x={-HANDLE_SIZE / 2}
            y={-HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            rx={2}
            fill="var(--color-card, #fff)"
            stroke={strokeColor}
            strokeWidth={HANDLE_BORDER_WIDTH}
            opacity={resolveDecorationOpacity(geometryInactive)}
            style={{ pointerEvents: 'none' }}
          />
          {/* The visible square above is already `pointerEvents: none`; this is the grab zone, so
              it is the only part a non-interactive chrome drops. */}
          {interactive && (
            <rect
              x={-HANDLE_HIT_PX / 2}
              y={-HANDLE_HIT_PX / 2}
              width={HANDLE_HIT_PX}
              height={HANDLE_HIT_PX}
              fill="transparent"
              style={{
                pointerEvents: 'all',
                cursor: geometryInactive
                  ? FROZEN_OBJECT_CURSOR
                  : getResizeCursorForHandle(handle.key, geometry.rotationDeg),
              }}
              onPointerDown={beginResize(handle.key)}
            />
          )}
        </g>
      ))}
    </>
  )
}
